import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import EmptyState from '../ui/EmptyState';
import ConfirmDialog from '../ui/ConfirmDialog';
import Modal from '../ui/Modal';
import Can from '../../auth/Can';
import { usePermission } from '../../auth/usePermission';
import { downloadDocument, verifyDocument, unverifyDocument, deleteDocument } from '../../api/documents';
import { pushToast } from '../ui/toastBus';
import { saveBlobResponse } from '../../utils/download';
import DocumentUploadModal from './DocumentUploadModal';

function formatSize(bytes) {
  if (!bytes) return '';
  const kb = bytes / 1024;
  return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`;
}

function DocumentPreviewModal({ doc, onClose, onVerify, verifying }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!doc) return undefined;

    let objectUrl;
    let cancelled = false;
    setStatus('loading');

    downloadDocument(doc.id)
      .then((response) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(response.data);
        setPreviewUrl(objectUrl);
        setPreviewType(response.data.type);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc]);

  return (
    <Modal
      open={!!doc}
      onClose={onClose}
      title={doc?.original_name || 'Document preview'}
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={onVerify} loading={verifying}>
            Verify
          </Button>
        </>
      }
    >
      {doc && status === 'loading' && <p className="py-8 text-center text-sm text-gray-500">Loading preview…</p>}
      {doc && status === 'error' && <p className="py-8 text-center text-sm text-red-600">Could not load preview.</p>}
      {doc && status === 'ready' && previewType?.startsWith('image/') && (
        <img src={previewUrl} alt={doc.original_name} className="mx-auto max-h-[70vh] max-w-full rounded-md" />
      )}
      {doc && status === 'ready' && previewType === 'application/pdf' && (
        // navpanes=0 tells the browser's built-in PDF viewer to keep its thumbnail/outline
        // sidebar closed by default, so it doesn't eat half the popup on multi-page PDFs.
        <iframe
          src={`${previewUrl}#toolbar=1&navpanes=0`}
          title={doc.original_name}
          className="h-[75vh] w-full rounded-md border border-gray-200"
        />
      )}
      {doc && status === 'ready' && previewType && !previewType.startsWith('image/') && previewType !== 'application/pdf' && (
        <p className="py-8 text-center text-sm text-gray-500">Preview not available for this file type — download to view it.</p>
      )}
    </Modal>
  );
}

export default function DocumentsTab({ candidate }) {
  const { can } = usePermission();
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] });

  const verifyMutation = useMutation({
    mutationFn: ({ id, verified }) => (verified ? unverifyDocument(id) : verifyDocument(id)),
    onSuccess: () => {
      invalidate();
      pushToast({ type: 'success', message: 'Document updated.' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => {
      invalidate();
      pushToast({ type: 'success', message: 'Document deleted.' });
      setDeleteTarget(null);
    },
  });

  const handleDownload = async (doc) => {
    try {
      const response = await downloadDocument(doc.id);
      saveBlobResponse(response, doc.original_name);
    } catch {
      pushToast({ type: 'error', message: 'Could not download document.' });
    }
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Can perm="documents.upload">
          <Button onClick={() => setUploadOpen(true)}>Upload document</Button>
        </Can>
      </div>

      {candidate.documents.length === 0 ? (
        <EmptyState title="No documents uploaded yet." />
      ) : (
        <div className="space-y-2">
          {candidate.documents.map((doc) => (
            <div key={doc.id} className="flex items-center justify-between rounded-md border border-gray-200 p-3">
              <div>
                <p className="text-sm font-medium text-gray-900">{doc.original_name}</p>
                <p className="text-xs text-gray-500">
                  {doc.document_type_name} · {formatSize(doc.size)} · uploaded by {doc.uploaded_by} on{' '}
                  {new Date(doc.created_at).toLocaleDateString()}
                  {doc.expiry_date && ` · expires ${new Date(doc.expiry_date).toLocaleDateString()}`}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge tone={doc.is_verified ? 'green' : 'gray'}>{doc.is_verified ? 'Verified' : 'Unverified'}</Badge>
                <button onClick={() => handleDownload(doc)} className="text-sm text-primary hover:underline">
                  Download
                </button>
                {can('documents.verify') && (
                  <button
                    onClick={() => (doc.is_verified ? verifyMutation.mutate({ id: doc.id, verified: true }) : setPreviewDoc(doc))}
                    className="text-sm text-primary hover:underline"
                  >
                    {doc.is_verified ? 'Unverify' : 'Verify'}
                  </button>
                )}
                <Can perm="documents.delete">
                  <button onClick={() => setDeleteTarget(doc)} className="text-sm text-red-600 hover:underline">
                    Delete
                  </button>
                </Can>
              </div>
            </div>
          ))}
        </div>
      )}

      <DocumentUploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} candidateId={candidate.id} />

      <DocumentPreviewModal
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
        verifying={verifyMutation.isPending}
        onVerify={() =>
          verifyMutation.mutate(
            { id: previewDoc.id, verified: false },
            { onSuccess: () => setPreviewDoc(null) }
          )
        }
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title="Delete document"
        message={`Delete ${deleteTarget?.original_name}?`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
